"use client";

import {
  Avatar,
  Box,
  Button,
  Flex,
  Popover,
  Portal,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";
import { LuUsers } from "react-icons/lu";

import {
  ChangeEvent,
  Menu,
  MenuContent,
  MenuItem,
} from "@components/common/Menu";
import { Tooltip } from "@components/ui/tooltip";
import EmployeesModal from "@features/back-office/employees/EmployeesModal";
import LoginModal from "@features/login/LoginModal";
import { NAVIGATION_PANEL_MIN_WIDTH_PX } from "config/layout/navigationPanel";
import { toPxString } from "helpers/styles";
import useControlledPopover from "hooks/useControlledPopover";
import useIsTruncated from "hooks/useIsTruncated";
import { ModalType } from "stores/pageStore";
import { useStores } from "stores/rootStore";
import PeaceSign from "vectors/PeaceSign";
import { useBasePath } from "hooks/useBasePath";

const LOGOUT_MENU_ITEM_VALUE = "logout";
const EMPLOYEES_MENU_ITEM_VALUE = "employees";

const UserAvatarMenu = () => {
  const { open, onToggle, onClose, triggerRef, contentRef } =
    useControlledPopover();
  const { userStore, pageStore } = useStores();
  const user = userStore.user;
  const { ref, isTruncated } = useIsTruncated();
  const basePath = useBasePath();
  const [employeesOpen, setEmployeesOpen] = useState(false);

  const onSelect = useCallback(
    (e: ChangeEvent) => {
      e.preventDefault();
      if (e.value === LOGOUT_MENU_ITEM_VALUE) {
        userStore.logOut({ basePath: basePath ?? "services" });
      }
      if (e.value === EMPLOYEES_MENU_ITEM_VALUE) {
        setEmployeesOpen(true);
      }
      onClose();
    },
    [onClose, userStore, basePath],
  );

  const onCloseEmployees = useCallback(() => {
    setEmployeesOpen(false);
  }, []);

  const onClickLogin = useCallback(() => {
    pageStore.openModal(ModalType.Login);
  }, [pageStore]);

  if (user == null) {
    return (
      <Flex alignItems="center" gap={2}>
        <Button size="2xs" variant="solid" onClick={onClickLogin}>
          Sign in
        </Button>
        <LoginModal />
      </Flex>
    );
  }

  const emailText = (
    <Box ref={ref} width={toPxString(NAVIGATION_PANEL_MIN_WIDTH_PX)} mb={2}>
      <Text
        color="text.secondary"
        fontWeight="medium"
        userSelect="none"
        truncate={isTruncated}
      >
        {user.email}
      </Text>
    </Box>
  );
  const textWithTooltip = isTruncated ? (
    <Tooltip content={user.email}>{emailText}</Tooltip>
  ) : (
    emailText
  );

  return (
    <Popover.Root open={open} onOpenChange={onToggle} lazyMount>
      <Popover.Trigger asChild>
        <Flex
          ref={triggerRef}
          flexDirection="row"
          alignItems="center"
          borderRadius="full"
          cursor="pointer"
        >
          <Avatar.Root size="2xs">
            <Avatar.Fallback name={user.name} />
            <Avatar.Image src={user.profileImage} />
          </Avatar.Root>
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            borderRadius={8}
            ref={contentRef}
            w="full"
            bgColor="surface.background"
            minHeight="fit-content"
            minW="xs"
            maxHeight="none"
            overflow="visible"
            p={2}
          >
            {textWithTooltip}
            <Menu onChange={onSelect} focus fontSize="xs">
              {basePath === "back-office" && (
                <MenuItem showSelected={false} value={EMPLOYEES_MENU_ITEM_VALUE}>
                  <MenuContent value="My Employees" icon={<LuUsers />} />
                </MenuItem>
              )}
              <MenuItem showSelected={false} value={LOGOUT_MENU_ITEM_VALUE}>
                <MenuContent value="Logout" icon={<PeaceSign />} />
              </MenuItem>
            </Menu>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>

      <EmployeesModal open={employeesOpen} onClose={onCloseEmployees} />
    </Popover.Root>
  );
};

export default observer(UserAvatarMenu);
